/**
 * DetailCard — shared card primitives for detail pages (publisher, title, etc.).
 *
 * Provides a consistent card shell, section header, body, info row, and KPI strip
 * used across admin detail pages. One change here propagates everywhere.
 */

// ── Card shell ───────────────────────────────────────────────────────────────

interface DetailCardProps {
  children: React.ReactNode;
  adminHighlight?: boolean;
}

export function DetailCard({ children, adminHighlight = false }: DetailCardProps) {
  return (
    <div
      className="bg-[var(--card)] rounded-[1.25rem] overflow-hidden"
      style={{
        border: adminHighlight
          ? "1px solid var(--terracotta-border, oklch(0.627 0.168 81.944 / 0.30))"
          : "1px solid var(--border-light)",
        boxShadow: "var(--shadow-card)",
      }}
    >
      {children}
    </div>
  );
}

// ── Card section header ──────────────────────────────────────────────────────

interface DetailCardHeadProps {
  children: React.ReactNode;
  tinted?: boolean;
}

export function DetailCardHead({ children, tinted = false }: DetailCardHeadProps) {
  return (
    <div
      className="px-4 sm:px-6 py-3.5 flex items-center justify-between shrink-0"
      style={{
        borderBottom: "1px solid var(--divider)",
        background: tinted ? "var(--terracotta-tint)" : "var(--surface-low)",
      }}
    >
      {children}
    </div>
  );
}

// ── Card section title label ─────────────────────────────────────────────────

export function DetailCardTitle({ children }: { children: React.ReactNode }) {
  return (
    <span
      className="text-[10px] font-bold uppercase tracking-[0.14em]"
      style={{ color: "var(--text-tertiary)" }}
    >
      {children}
    </span>
  );
}

// ── Card body container ──────────────────────────────────────────────────────

export function DetailCardBd({ children }: { children: React.ReactNode }) {
  return <div className="px-4 sm:px-6 py-[18px]">{children}</div>;
}

// ── Label/value info row ─────────────────────────────────────────────────────

interface DetailRowProps {
  label: string;
  value: React.ReactNode;
}

export function DetailRow({ label, value }: DetailRowProps) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-[120px_1fr] gap-y-0.5 sm:gap-1 mb-[10px] last:mb-0">
      <span className="text-[12px]" style={{ color: "var(--text-tertiary)" }}>{label}</span>
      <span className="text-[13.5px] font-medium break-words" style={{ color: "var(--text-primary)" }}>{value}</span>
    </div>
  );
}

// ── KPI strip (horizontal stat row) ─────────────────────────────────────────

export interface KpiStat {
  label: string;
  value: string | number;
  color?: string;
}

export function KpiStrip({ stats }: { stats: KpiStat[] }) {
  return (
    <div
      className="grid grid-cols-2 sm:grid-cols-4 [&>*:nth-child(2n)]:border-e-0 sm:[&>*]:border-e sm:[&>*:nth-child(2n)]:border-e sm:[&>*:last-child]:border-e-0 [&>*:nth-last-child(-n+2)]:border-b-0 sm:[&>*]:border-b-0"
      style={{ background: "var(--card)" }}
    >
      {stats.map((s) => (
        <div
          key={s.label}
          className="px-4 sm:px-6 py-4 sm:py-5 text-center border-e border-b"
          style={{ borderColor: "var(--border-light)" }}
        >
          <div
            className="text-[24px] sm:text-[28px] font-extrabold leading-none"
            style={{ color: s.color ?? "var(--text-primary)", letterSpacing: "-0.02em" }}
          >
            {s.value}
          </div>
          <div
            className="text-[9.5px] font-bold uppercase tracking-[0.12em] mt-[6px]"
            style={{ color: "var(--text-tertiary)" }}
          >
            {s.label}
          </div>
        </div>
      ))}
    </div>
  );
}
